import React, { ChangeEvent, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import moment from 'moment';
import { Theme } from '@app/theme';
import { Input } from '@app/form';

import { BaseEvent } from './Calendar';
import { useTranslation } from 'react-i18next';
import { EventPopoverTitle } from './EventPopoverTitle';

const useStyles = createUseStyles((theme: Theme) => ({
  content: {
    padding: theme.marginBase * 2,
  },
  label: {
    ...theme.fonts.label,
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase / 2,
  },
  inputLine: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.marginBase * 2,
    gap: theme.marginBase,
  },
  input: {
    maxWidth: theme.marginBase * 20,
  },
}));

export interface PopoverInfo {
  coords: {
    x: number;
    y: number;
  };
  eventId: string;
}

interface Props {
  event?: BaseEvent;
  onEditEvent?: (event: BaseEvent) => any;
  timezone: string;
  minDuration: number;
  fixedDuration?: boolean;
}

export const EventEditPopover = ({
  event,
  onEditEvent,
  timezone,
  minDuration,
  fixedDuration,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const onStartDateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!onEditEvent || !event) {
        return;
      }
      const date = moment.tz(e.currentTarget.value, 'YYYY-MM-DD', timezone);
      if (!date.isValid()) {
        return;
      }
      const start = moment(event.start).tz(timezone);
      const originalDuration = moment(event.end).diff(start, 'minutes');
      date.hours(start.hours()).minutes(start.minutes());

      if (moment().isAfter(date)) {
        return;
      }

      onEditEvent({
        ...event,
        start: date.toDate(),
        end: date.add(originalDuration, 'minutes').toDate(),
      });
    },
    [onEditEvent, event, timezone],
  );

  const onEndDateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!onEditEvent || !event || fixedDuration) {
        return;
      }
      const date = moment.tz(e.currentTarget.value, 'YYYY-MM-DD', timezone);
      if (!date.isValid()) {
        return;
      }
      const end = moment(event.end).tz(timezone);
      date.hours(end.hours()).minutes(end.minutes());

      if (moment(event.start).add(minDuration, 'minutes').isAfter(date)) {
        return;
      }

      onEditEvent({
        ...event,
        end: date.toDate(),
      });
    },
    [onEditEvent, event, timezone, minDuration, fixedDuration],
  );

  const onStartTimeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!onEditEvent || !event) {
        return;
      }
      const time = moment.tz(e.currentTarget.value, 'HH:mm', timezone);
      if (!time.isValid()) {
        return;
      }
      const date = moment(event.start).tz(timezone);
      date.hours(time.hours()).minutes(time.minutes());
      const originalDuration = moment(event.end).diff(event.start, 'minutes');

      if (moment().isAfter(date)) {
        return;
      }

      onEditEvent({
        ...event,
        start: date.toDate(),
        end: date.add(originalDuration, 'minutes').toDate(),
      });
    },
    [onEditEvent, event, timezone],
  );

  const onEndTimeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!onEditEvent || !event || fixedDuration) {
        return;
      }
      const time = moment.tz(e.currentTarget.value, 'HH:mm', timezone);
      if (!time.isValid()) {
        return;
      }
      const date = moment(event.end).tz(timezone);
      date.hours(time.hours()).minutes(time.minutes());
      if (moment(event.start).add(minDuration, 'minutes').isAfter(date)) {
        return;
      }

      onEditEvent({
        ...event,
        end: date.toDate(),
      });
    },
    [onEditEvent, event, timezone, minDuration, fixedDuration],
  );

  if (!onEditEvent || !event) {
    return null;
  }

  const startDate = moment.tz(event.start, timezone).format('YYYY-MM-DD');
  const startTime = moment.tz(event.start, timezone).format('HH:mm');
  const endDate = moment.tz(event.end, timezone).format('YYYY-MM-DD');
  const endTime = moment.tz(event.end, timezone).format('HH:mm');

  return (
    <div className={classes.content}>
      <EventPopoverTitle
        title={t('calendar.popover.title')}
        icon="ri-edit-line"
      />

      <label htmlFor="start-date" className={classes.label}>
        {t('calendar.popover.startDate')}
      </label>
      <div className={classes.inputLine}>
        <Input
          noMargin
          small
          type="date"
          className={classes.input}
          value={startDate}
          id="start-date"
          name="startDate"
          onChange={onStartDateChange}
          min={moment().format('YYYY-MM-DD')}
        />
        <Input
          noMargin
          small
          type="time"
          className={classes.input}
          value={startTime}
          name="startTime"
          onChange={onStartTimeChange}
        />
      </div>

      <label htmlFor="end-date" className={classes.label}>
        {t('calendar.popover.endDate')}
      </label>
      <div className={classes.inputLine}>
        <Input
          noMargin
          small
          type="date"
          className={classes.input}
          value={endDate}
          id="end-date"
          name="endDate"
          onChange={onEndDateChange}
          disabled={fixedDuration}
        />
        <Input
          noMargin
          small
          type="time"
          className={classes.input}
          value={endTime}
          name="endTime"
          onChange={onEndTimeChange}
          disabled={fixedDuration}
        />
      </div>
    </div>
  );
};
